import React from 'react';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { useIntl } from 'react-intl';
import { ExtractionConfidenceBadge } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/Graphs/Ranges/ExtractionConfidenceBadge';
import { memberDetailMessages } from './messages';
import {
  getDocumentExtractionV2ReviewState,
  isDocumentExtractionV2Resource,
} from './ExtractedResourceProvenance';

/**
 * FHIR tag systems that describe where a clinical resource came from. We prefer
 * the most specific, human-readable source first.
 */
export const FHIR_DEVICE_SOURCE_TAG_SYSTEM =
  'http://lifeomic.com/fhir/device-source';
export const FHIR_SOURCE_TYPE_TAG_SYSTEM =
  'http://lifeomic.com/fhir/source-type';
export const FHIR_SOURCE_TAG_SYSTEM = 'http://lifeomic.com/fhir/source';

type MetaTag = { system?: string; code?: string; display?: string };

type ProvenanceResource = fhir.Resource & {
  meta?: { tag?: MetaTag[] };
  extension?: fhir.Extension[];
  identifier?: Array<{ system?: string; value?: string }>;
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    maxWidth: '100%',
  },
  zoriLabel: {
    fontWeight: theme.typography.fontWeightMedium,
    whiteSpace: 'nowrap',
  },
  source: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.pxToRem(12),
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: theme.typography.caption.lineHeight,
    whiteSpace: 'nowrap',
  },
  needsReview: {
    backgroundColor: theme.palette.orange[100],
    color: theme.palette.orange[800],
    border: `1px solid ${theme.palette.orange[300]}`,
  },
  approved: {
    backgroundColor: theme.palette.green[100],
    color: theme.palette.green[800],
    border: `1px solid ${theme.palette.green[300]}`,
  },
}));

const findTag = (
  resource: ProvenanceResource,
  system: string,
): MetaTag | undefined =>
  resource.meta?.tag?.find(tag => tag.system === system);

/**
 * Derives a human-readable source label from a resource's meta tags, preferring
 * the most descriptive value: device-source (e.g. "HealthKit"), then
 * source-type (e.g. "Elation Problem", "Member File"), then source (e.g.
 * "LifeOmic EHR").
 */
export const getResourceSourceLabel = (
  resource: ProvenanceResource,
): string | null => {
  for (const system of [
    FHIR_DEVICE_SOURCE_TAG_SYSTEM,
    FHIR_SOURCE_TYPE_TAG_SYSTEM,
    FHIR_SOURCE_TAG_SYSTEM,
  ]) {
    const tag = findTag(resource, system);
    const label = tag?.display || tag?.code;
    if (label) {
      return label;
    }
  }

  return null;
};

/**
 * Provenance cell shown in the member-detail clinical tabs. AI-extracted
 * resources render a "Zori AI" label plus a review-status badge; everything
 * else falls back to the resource's known source (Elation, member upload,
 * HealthKit, etc.).
 */
export const MemberResourceProvenance: React.FC<{
  resource: ProvenanceResource;
}> = ({ resource }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  if (isDocumentExtractionV2Resource(resource)) {
    const state = getDocumentExtractionV2ReviewState(resource);
    const isApproved = state === 'approved';
    const statusLabel = isApproved
      ? formatMessage(
          memberDetailMessages.memberDocumentsExtractionReviewStatusApproved,
        )
      : formatMessage(
          memberDetailMessages.memberDocumentsExtractionReviewStatusNeedsReview,
        );

    return (
      <span className={classes.container}>
        <span className={classes.zoriLabel}>
          {formatMessage(memberDetailMessages.memberDocumentsProvenanceZoriAi)}
        </span>
        <span
          className={`${classes.badge} ${
            isApproved ? classes.approved : classes.needsReview
          }`}
        >
          {statusLabel}
        </span>
        <ExtractionConfidenceBadge resource={resource} />
      </span>
    );
  }

  const sourceLabel = getResourceSourceLabel(resource);
  if (!sourceLabel) {
    return null;
  }

  return <span className={classes.source}>{sourceLabel}</span>;
};
